/* global google */
import type { LocationAutocompleteSuggestion } from '../../types/location/geocoding.type';

type GoogleAddressRecord = Pick<
  google.maps.GeocoderAddressComponent,
  'long_name' | 'short_name' | 'types'
>;

interface GoogleLocationRecord {
  lat: () => number;
  lng: () => number;
}

interface GoogleGeocodedRecord {
  address_components?: GoogleAddressRecord[];
  formatted_address?: string;
  geometry?: {
    location?: GoogleLocationRecord;
  };
  place_id?: string;
}

interface GooglePlaceAddressComponentRecord {
  longText?: string | null;
  shortText?: string | null;
  types?: string[];
}

interface GooglePlaceRecord {
  addressComponents?: GooglePlaceAddressComponentRecord[];
  displayName?: string | null;
  formattedAddress?: string | null;
  id?: string | null;
  location?: GoogleLocationRecord | null;
}

const normalizeText = (value?: string | null): string | null => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
};

const findAddressComponent = (
  components: GoogleAddressRecord[] | undefined,
  types: string[]
): GoogleAddressRecord | undefined => {
  if (!components?.length) {
    return undefined;
  }

  return components.find((component) =>
    types.every((type) => component.types.includes(type))
  );
};

const getAddressComponentLongName = (
  components: GoogleAddressRecord[] | undefined,
  types: string[]
): string | null => {
  return normalizeText(findAddressComponent(components, types)?.long_name);
};

const getFirstAddressComponentLongName = (
  components: GoogleAddressRecord[] | undefined,
  typeVariants: string[][]
): string | null => {
  for (const types of typeVariants) {
    const value = getAddressComponentLongName(components, types);
    if (value) {
      return value;
    }
  }

  return null;
};

const getAddressComponentShortName = (
  components: GoogleAddressRecord[] | undefined,
  types: string[]
): string | null => {
  return normalizeText(findAddressComponent(components, types)?.short_name);
};

/**
 * Maps a Google geocoded place into the shared normalized location suggestion
 * shape consumed by the property editor.
 */
export const mapGoogleGeocodedRecordToSuggestion = (
  place: GoogleGeocodedRecord
): LocationAutocompleteSuggestion | null => {
  const latitude = place.geometry?.location?.lat();
  const longitude = place.geometry?.location?.lng();
  const label = normalizeText(place.formatted_address);
  const placeId = normalizeText(place.place_id);

  if (
    latitude == null ||
    longitude == null ||
    !Number.isFinite(latitude) ||
    !Number.isFinite(longitude) ||
    !label ||
    !placeId
  ) {
    return null;
  }

  const components = place.address_components;

  return {
    id: placeId,
    label,
    latitude,
    longitude,
    countryCode: getAddressComponentShortName(components, [
      'country',
    ])?.toUpperCase(),
    region: getAddressComponentLongName(components, [
      'administrative_area_level_1',
    ]),
    locality: getFirstAddressComponentLongName(components, [
      ['locality'],
      ['administrative_area_level_3'],
      ['postal_town'],
      ['administrative_area_level_2'],
    ]),
    neighborhood: getFirstAddressComponentLongName(components, [
      ['neighborhood'],
      ['sublocality'],
      ['sublocality_level_1'],
      ['sublocality_level_2'],
      ['sublocality_level_3'],
      ['sublocality_level_4'],
      ['colloquial_area'],
      ['administrative_area_level_4'],
    ]),
    streetName: getAddressComponentLongName(components, ['route']),
    streetNumber: getAddressComponentLongName(components, ['street_number']),
  };
};

/**
 * Maps a Google Places `Place` payload into the shared normalized location
 * suggestion shape consumed by the property editor.
 */
export const mapGooglePlaceRecordToSuggestion = (
  place: GooglePlaceRecord
): LocationAutocompleteSuggestion | null => {
  const latitude = place.location?.lat();
  const longitude = place.location?.lng();
  const label = normalizeText(place.formattedAddress ?? place.displayName);
  const placeId = normalizeText(place.id);

  if (
    latitude == null ||
    longitude == null ||
    !Number.isFinite(latitude) ||
    !Number.isFinite(longitude) ||
    !label ||
    !placeId
  ) {
    return null;
  }

  const components =
    place.addressComponents?.map((component) => ({
      long_name: component.longText ?? '',
      short_name: component.shortText ?? '',
      types: component.types ?? [],
    })) ?? [];

  return {
    id: placeId,
    label,
    latitude,
    longitude,
    countryCode: getAddressComponentShortName(components, [
      'country',
    ])?.toUpperCase(),
    region: getAddressComponentLongName(components, [
      'administrative_area_level_1',
    ]),
    locality: getFirstAddressComponentLongName(components, [
      ['locality'],
      ['administrative_area_level_3'],
      ['postal_town'],
      ['administrative_area_level_2'],
    ]),
    neighborhood: getFirstAddressComponentLongName(components, [
      ['neighborhood'],
      ['sublocality'],
      ['sublocality_level_1'],
      ['sublocality_level_2'],
      ['sublocality_level_3'],
      ['sublocality_level_4'],
      ['colloquial_area'],
      ['administrative_area_level_4'],
    ]),
    streetName: getAddressComponentLongName(components, ['route']),
    streetNumber: getAddressComponentLongName(components, ['street_number']),
  };
};
